import React, { useState, useEffect, useCallback } from "react";
import EvaluationEngine from "./components/evaluation/EvaluationEngine";
import SolHomePage from "./pages/SolHomePage.jsx";
import IframeTester from "./components/IframeTester";
import ErrorDisplay from "./components/shared/ErrorDisplay";
import SessionManager from "./components/SessionManager";
import OnboardingPage from "./components/Onboarding/OnboardingPage";
import { useIframeMessaging } from './utils/iframeMessageHandler';
import { setWithExpiry, remove, decodeJwt } from './utils/sessionStorage';
import { setupSplashFallbackHide, hideNativeSplash } from './utils/nativeApiUtils';
import { shouldShowOnboarding, markOnboardingCompleted, markOnboardingSkipped } from './utils/onboardingUtils';
import { requestInitialPermissions } from './utils/permissionsUtils';

/**
 * App - Main Sol application entry point
 * Routes to SolHomePage for the main provider workspace
 */
function App() {
  // Application state
  const [appState, setAppState] = useState('loading');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);

  // Session data from URL params or iframe messaging
  const [sessionToJoin, setSessionToJoin] = useState("");
  const [zoomTopic, setZoomTopic] = useState("");
  const [zoomPasscode, setZoomPasscode] = useState("");
  const [miraSolCaseId, setMiraSolCaseId] = useState(null);
  const [coreServerCaseId, setCoreServerCaseId] = useState(null);
  const [providerInfo, setProviderInfo] = useState(null);
  const [healthcareInfo, setHealthcareInfo] = useState(null);
  const [iframeAuthData, setIframeAuthData] = useState(null);

  // Check if onboarding should be shown after authentication
  const checkOnboardingStatus = useCallback(async () => {
    if (!onboardingChecked) {
      try {
        const shouldShow = await shouldShowOnboarding();
        setShowOnboarding(shouldShow);
        setOnboardingChecked(true);
        if (!shouldShow) {
          await requestInitialPermissions();
        }
        console.log('[App] Onboarding check completed:', { shouldShow });
      } catch (error) {
        console.error('[App] Error checking onboarding status:', error);
        setOnboardingChecked(true);
      }
    }
  }, [onboardingChecked]);

  // Native Splash Screen handling (Capacitor)
  useEffect(() => {
    // 1. Hide splash screen fallback
    const cleanupSplash = setupSplashFallbackHide(8000);
    
    // 2. Start background-exception logging
    const { setupBackgroundExceptionLogger } = require('./utils/backgroundExceptionLogger');
    const cleanupBgLogger = setupBackgroundExceptionLogger();

    return () => {
      // Cleanup both listeners when App unmounts (unlikely in SPA but safe)
      cleanupSplash?.();
      cleanupBgLogger?.();
    };
  }, []);

  // Hide splash when app becomes ready or if an error occurs
  useEffect(() => {
    // Don't hide splash until onboarding check completes
    if ((appState === 'ready' && (!showOnboarding || onboardingChecked)) || error) {
      hideNativeSplash();
    }
  }, [appState, error, showOnboarding, onboardingChecked]);

  // Helper functions for localStorage
  const saveAuthDataToStorage = (authData, tokenInfo) => {
    try {
      let expiryTime = null;
      if (tokenInfo && tokenInfo.expires_at) {
        const tokenExpiry = new Date(tokenInfo.expires_at);
        const now = new Date();
        const ttl = tokenExpiry.getTime() - now.getTime();
        if (ttl > 0) {
          expiryTime = ttl;
        } else {
          console.warn('Token is already expired, not saving to localStorage');
          return;
        }
      }
      
      // Decode the JWT token for compatibility with existing code
      const decodedJwt = authData.api_token ? decodeJwt(authData.api_token) : null;
      const authDataWithDecoded = {
        ...authData,
        decoded_jwt: decodedJwt
      };
      
      setWithExpiry('sol_auth_data', authDataWithDecoded, expiryTime);
    } catch (error) {
      console.error('Error saving auth data to localStorage:', error);
    }
  };

  const clearAuthDataFromStorage = () => {
    try {
      remove('sol_auth_data');
    } catch (error) {
      // console.error('Error clearing auth data from localStorage:', error);
    }
  };

  // Memoize callbacks to prevent infinite re-renders
  const handleProviderAuth = useCallback(async (payload) => {
    // console.log('Provider authentication received via iframe:', payload);
    setIframeAuthData(payload);
    
    // Validate the API token with backend via SessionManager
    if (payload.api_token) {
      // console.log('Sending token validation request to SessionManager...');
      window.postMessage({
        type: 'VALIDATE_TOKEN_REQUEST',
        payload: {
          api_token: payload.api_token,
          request_id: 'iframe_auth_' + Date.now()
        }
      }, window.location.origin);
    }
    
    // Save auth data to localStorage with token expiry if available
    if (payload.token_info) {
      saveAuthDataToStorage(payload, payload.token_info);
    }
    
    // Extract case data and set up Sol for the provider session
    if (payload.mirasol_case_id) {
      setMiraSolCaseId(payload.mirasol_case_id);
    }
    if (payload.core_server_case_id) {
      setCoreServerCaseId(payload.core_server_case_id);
    }
    if (payload.provider_info) {
      setProviderInfo(payload.provider_info);
    }
    
    // Handle health system info for iframe mode
    if (payload.health_system_info) {
      setHealthcareInfo(payload.health_system_info);
    } else {
      // Set minimal healthcare info to prevent waiting indefinitely
      setHealthcareInfo({ id: 'iframe-health-system', name: 'Healthcare System' });
    }
    
    // Handle Zoom session details if provided
    if (payload.zoom_session) {
      setZoomTopic(payload.zoom_session.topic || 'Video Visit');
      setZoomPasscode(payload.zoom_session.passcode || '');
    }
    // Don't set app state to ready yet - wait for token validation
    // setAppState('ready'); 
  }, []);

  const handleCaseData = useCallback((payload) => {
    return
  }, []);

  const handleError = useCallback((error) => {
    setError(error.message || 'Iframe communication error');
  }, []);

  // Set up iframe messaging for provider authentication
  const { isIframeMode, connectionStatus } = useIframeMessaging({
    onProviderAuth: handleProviderAuth,
    onCaseData: handleCaseData,
    onError: handleError
  });

  // Check for URL parameters when component mounts
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionParam = urlParams.get('session');
    const topicParam = urlParams.get('topic');
    const passcodeParam = urlParams.get('passcode');
    const attendeeNameParam = urlParams.get('attendeeName');

    // Helper function to decode base64 with double encoding support
    const decodeParam = (param) => {
      if (!param) return param;
      try {
        let decoded = atob(param);
        if (decoded.startsWith('/') || decoded.includes('//')) {
          decoded = atob(decoded);
        }
        return decoded;
      } catch (error) {
        return param;
      }
    };

    let timeoutId;

    const handleUrlParamsWithOnboarding = async () => {
      // Check if we have topic and passcode for direct join
      if (topicParam && passcodeParam) {
        const decodedTopic = decodeParam(topicParam);
        const decodedPasscode = decodeParam(passcodeParam);
        const decodedAttendeeName = decodeParam(attendeeNameParam) || 'Provider';

        console.log('Decoded parameters:', {
          topic: decodedTopic,
          passcode: decodedPasscode,
          attendeeName: decodedAttendeeName
        });

        setZoomTopic(decodedTopic);
        setZoomPasscode(decodedPasscode);

        // Check onboarding before setting ready
        await checkOnboardingStatus();
        setAppState('ready');
        return;
      }

      // Legacy session parameter handling
      if (sessionParam) {
        console.log('Session ID found in URL:', sessionParam);
        setSessionToJoin(sessionParam);

        // Check onboarding before setting ready
        await checkOnboardingStatus();
        setAppState('ready');

        // Remove the session parameter from URL to avoid rejoining on refresh
        const newUrl = window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
        return;
      }

      // If no session ID in URL, set a timeout to transition to 'ready' state for standalone use
      timeoutId = setTimeout(async () => {
        await checkOnboardingStatus();
        setAppState('ready');
      }, 2000); // Wait 2 seconds for potential postMessage
    };

    (async () => {
      try {
        await handleUrlParamsWithOnboarding();
      } catch (error) {
        console.error('[App] URL params handling failed:', error);
        setError('Failed to initialize app');
        setAppState('ready'); // Fail open
      }
    })();

    // Return cleanup from useEffect
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [checkOnboardingStatus]);

  // Listen for messages from the parent window (Core Server)
  useEffect(() => {
    const handleMessage = async (event) => {
      const { type, payload } = event.data;

      if (type === 'initiateVideoVisit') {
        try {
          console.log('Processing initiateVideoVisit message:', payload);

          if (payload && payload.zoom_session_id && payload.latest_zoom_meeting &&
              payload.miraSolCaseId && payload.providerInfo && payload.healthcareInfo) {
            setSessionToJoin(payload.zoom_session_id);
            setZoomTopic(payload.latest_zoom_meeting.topic);
            setZoomPasscode(payload.latest_zoom_meeting.passcode);
            setMiraSolCaseId(payload.miraSolCaseId);
            setProviderInfo(payload.providerInfo);
            setHealthcareInfo(payload.healthcareInfo);

            // Check onboarding before setting ready
            await checkOnboardingStatus();
            setAppState('ready');
          } else {
            throw new Error('Missing required video visit data');
          }
        } catch (error) {
          console.error('[App] Message handling failed:', error);
          setError('Failed to process session initialization');
          setAppState('ready'); // Fail open - let user see error
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [checkOnboardingStatus]);

  // Handle clearing authentication state
  const handleClearAuth = () => {
    clearAuthDataFromStorage();
    setIframeAuthData(null);
    setMiraSolCaseId(null);
    setProviderInfo(null);
    setHealthcareInfo(null);
    setSessionToJoin('');
    setZoomTopic('');
    setZoomPasscode('');
    setAppState('loading');
    setSuccess('Authentication cleared successfully');
  };

  // Handle reset session
  const handleResetSession = useCallback(() => {
    remove('zoomActiveSessionDetails');
    // Reload page immediately
    window.location.reload();
  }, []);


  // Dismiss notifications
  const dismissError = useCallback(() => setError(null), []);
  const dismissSuccess = useCallback(() => setSuccess(null), []);

  // Onboarding handlers
  const handleOnboardingComplete = useCallback(async () => {
    markOnboardingCompleted();
    setShowOnboarding(false);
    await requestInitialPermissions();
    console.log('[App] Onboarding completed');
  }, []);

  const handleOnboardingSkip = useCallback(async () => {
    markOnboardingSkipped();
    setShowOnboarding(false);
    await requestInitialPermissions();
    console.log('[App] Onboarding skipped');
  }, []);

  // Handle token validation success from SessionManager
  const handleTokenValidated = useCallback((authResult) => {
    console.log('Token validated by SessionManager:', authResult);

    if (authResult && authResult.token_info && authResult.token_info.success) {
      // Extract provider info from the validated token or use iframe data
      if (authResult.provider_data) {
        setProviderInfo({
          id: authResult.provider_data.id,
          name: authResult.provider_data.name,
          specialty: authResult.provider_data.specialty || 'No Specialty'
        });
      } else if (authResult.decoded_jwt && authResult.decoded_jwt.provider_id) {
        // Use provider info from iframe auth data if available
        if (iframeAuthData?.provider_info) {
          setProviderInfo(iframeAuthData.provider_info);
        } else {
          // Fallback to minimal provider info from JWT
          setProviderInfo({
            id: authResult.decoded_jwt.provider_id,
            name: 'Provider',
            specialty: 'Healthcare Provider'
          });
        }
      }

      // Set healthcare info if not already set
      if (!healthcareInfo && iframeAuthData?.health_system_info) {
        setHealthcareInfo(iframeAuthData.health_system_info);
      }

      // Check onboarding status asynchronously in background
      (async () => {
        try {
          await checkOnboardingStatus();
        } catch (error) {
          console.error('[App] Onboarding check failed after token validation:', error);
          setError('Failed to complete initialization');
          setAppState('ready'); // Fail open
        }
      })();

      // Set app state to ready after successful validation
      setAppState('ready');
      console.log('App state set to ready after token validation');
    } else if (authResult && authResult.cleared) {
      // Token was cleared
      setAppState('loading');
      setProviderInfo(null);
      setShowOnboarding(false);
      setOnboardingChecked(false);
    }
  }, [iframeAuthData, healthcareInfo, checkOnboardingStatus]);

  // Handle SessionManager errors
  const handleSessionManagerError = useCallback((error) => {
    setError(error.message);
  }, []);

  return (
    <div className="sol-app">
      {/* Session Manager for authentication */}
      <SessionManager 
        onTokenValidated={handleTokenValidated}
        onError={handleSessionManagerError}
      />

      {/* Error/Success Notifications */}
      <ErrorDisplay
        error={error}
        success={success}
        onDismiss={error ? dismissError : dismissSuccess}
        position="top-center"
      />

      {/* Development Tools */}
      {process.env.NODE_ENV === 'development' && <IframeTester />}

      {/* Application Header */}
      <header className="sol-header">
        <div className="header-content">
          <div className="header-left">
            <h1>Sol, the Ankr Scribe</h1>
            <p>Writing your notes on phone, video, or in the clinic!</p>
          </div>
          
          <div className="header-right">
            <div className="connection-status">
              <span className={`status-indicator ${isIframeMode ? 'connected' : 'disconnected'}`}></span>
              <span className="status-text">
                {isIframeMode ? 'Core Server Connected' : 'Standalone Mode'}
              </span>
            </div>

            <div className="header-controls">
              <button
                onClick={handleResetSession}
                title="Reset session and reload page"
              >
               <i className="fas fa-refresh"></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Onboarding Modal */}
      {showOnboarding && onboardingChecked && (
        <OnboardingPage
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
        />
      )}

      {/* Main Application Content */}
      <main className="sol-main">
        {appState === 'loading' && (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading Sol workspace...</p>
            <small>Waiting for visit details or session parameters</small>
          </div>
        )}

        {appState === 'ready' && !showOnboarding && (
          <SolHomePage
            sessionToJoin={sessionToJoin}
            zoomTopic={zoomTopic}
            zoomPasscode={zoomPasscode}
            miraSolCaseId={miraSolCaseId}
            coreServerCaseId={coreServerCaseId}
            providerInfo={providerInfo}
            healthcareInfo={healthcareInfo}
          />
        )}
      </main>

    </div>
  );
}

export default App;